import { useState, useCallback, useEffect, useRef } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import Layout from './components/Layout';
import PurchaseOrders from './pages/PurchaseOrders';
import PurchaseOrderDetails from './pages/PoDetailPage';
import PurchaseOrderComments from './pages/PoCommentPage';
import AppOffline from './components/AppOffline';
import { APP_OFFLINE } from './utils/constants';
import * as serviceWorkerRegistration from "./serviceWorkerRegistration";
import UpdateDialog from './components/UpdateDialog';

const App = () => {
  const [loading, setLoading] = useState(false);
  const [snackbars, setSnackbars] = useState([]);
  const [showUpdateDialog, setShowUpdateDialog] = useState(false);
  const [waitingWorker, setWaitingWorker] = useState(null);
  const reminderTimeoutRef = useRef(null);


  const onServiceWorkerUpdate = useCallback((registration) => {
    if (reminderTimeoutRef.current) {
      clearTimeout(reminderTimeoutRef.current);
      reminderTimeoutRef.current = null;
    }
    setWaitingWorker(registration.waiting);
    setShowUpdateDialog(true);
  }, []);

  useEffect(() => {
    serviceWorkerRegistration.register({ onUpdate: onServiceWorkerUpdate });

    const controllerChangeHandler = () => {
      window.location.reload();
    };

    navigator.serviceWorker?.addEventListener(
      "controllerchange",
      controllerChangeHandler
    );

    return () => {
      if (reminderTimeoutRef.current) {
        clearTimeout(reminderTimeoutRef.current);
      }
      navigator.serviceWorker?.removeEventListener(
        "controllerchange",
        controllerChangeHandler
      );
    };
  }, [onServiceWorkerUpdate]);

  const handleCloseUpdateDialog = () => {
    setShowUpdateDialog(false);
  };

  const handleRefresh = () => {
    if (reminderTimeoutRef.current) {
      clearTimeout(reminderTimeoutRef.current);
      reminderTimeoutRef.current = null;
    }
    if (waitingWorker) {
      waitingWorker.postMessage({ type: "SKIP_WAITING" });
      setShowUpdateDialog(false);
    } else {
      setShowUpdateDialog(false);
    }
  };

  const handleRemindLater = () => {
    if (reminderTimeoutRef.current) {
      clearTimeout(reminderTimeoutRef.current);
    }
    setShowUpdateDialog(false);
    reminderTimeoutRef.current = setTimeout(() => {
      setShowUpdateDialog(true);
      reminderTimeoutRef.current = null;
    }, 5 * 60 * 1000);
  };

  const addSnackbar = useCallback((message, isSuccess = true, ms = 5) => {
    setSnackbars((prev) => [
      {
        id: new Date().getTime(),
        message: message,
        severity: isSuccess ? 'success' : 'error',
        ms: ms * 1000,
      },
      ...prev,
    ]);
  }, []);

  const content = APP_OFFLINE ? (
    <Route index element={<AppOffline />} />
  ) : (
    <>
      <Route
        index
        element={<PurchaseOrders setLoading={setLoading} addSnackbar={addSnackbar} />}
      />
      <Route
        path="purchase-orders"
        element={<PurchaseOrders setLoading={setLoading} addSnackbar={addSnackbar} />}
      />
      <Route
        path="purchase-orders/:id"
        element={<PurchaseOrderDetails setLoading={setLoading} addSnackbar={addSnackbar} />}
      />
      <Route
        path="purchase-orders/:id/comments"
        element={<PurchaseOrderComments setLoading={setLoading} addSnackbar={addSnackbar} />}
      />
    </>
  );

  return (
    <>
      <Router>
        <Routes>
          <Route
            path="/"
            element={<Layout loading={loading} snackbars={snackbars} setSnackbars={setSnackbars} />}
          >
            {content}
          </Route>
        </Routes>
      </Router>
      <UpdateDialog
        open={showUpdateDialog}
        onClose={handleCloseUpdateDialog}
        onRefresh={handleRefresh}
        onRemindLater={handleRemindLater}
      />
    </>
  );
};

export default App;